import {create} from 'zustand'
import {useShallow} from 'zustand/react/shallow'

import {
  ACCOUNT_TYPE_SAVINGS,
  EMPTY_LOCAL_FIELDS,
  PaymentFormValues,
} from '@progyny/pages/Budget/Reimbursement/helpers/PaymentFormModel'

export type Document = {
  file: File
  docType: string
  description?: string
}

export type ExistingClaimDocument = {
  id: string
  fileName: string
  fileUrl?: string
  documentType: string
  updatedAtDate: string
  description?: string
}

const defaultBankDetails: PaymentFormValues = {
  country: '',
  accountHolderName: '',
  accountNumber: '',
  swiftCode: '',
  bankName: '',
  accountType: ACCOUNT_TYPE_SAVINGS,
  localFields: EMPTY_LOCAL_FIELDS,
}

// Values that can be seeded when entering the claim flow (e.g. when editing an
// existing claim). Replaces the `initial*` provider props.
export type ClaimSeed = {
  claimRequested?: string
  claimId?: string
  treatment?: string
  treatmentCadence?: string
  additionalInfo?: string
  existingDocuments?: ExistingClaimDocument[]
}

interface ClaimState {
  // Claim
  claimRequested: string
  claimId: string
  // Treatment
  treatment: string
  treatmentCadence: string
  additionalInfo: string
  treatmentLabel: string
  // Documents
  docType: string
  documents: Document[]
  documentDescription: string
  docIdType: string
  idDocuments: Document[]
  isConsetSelected: boolean
  isMemberIDRemoved: boolean
  existingDocuments: ExistingClaimDocument[]
  // Medical info
  medicallyNecessary: boolean
  // Bank details
  bankDetails: PaymentFormValues
  // Address
  homeAddress: string
  city: string
  country: string
  postalCode: string

  // Actions - Claim
  setClaimRequested: (value: string) => void
  setClaimId: (value: string) => void
  // Actions - Treatment
  setTreatment: (value: string) => void
  setTreatmentCadence: (value: string) => void
  setAdditionalInfo: (value: string) => void
  setTreatmentLabel: (value: string) => void
  // Actions - Documents
  setDocType: (value: string) => void
  setDocuments: (value: Document[]) => void
  setDocumentDescription: (value: string) => void
  setDocIdType: (value: string) => void
  setIdDocuments: (value: Document[]) => void
  setConsetSelected: (value: boolean) => void
  setMemberIDRemoved: (value: boolean) => void
  setExistingDocuments: (value: ExistingClaimDocument[]) => void
  // Actions - Medical info
  setMedicallyNecessary: (value: boolean) => void
  // Actions - Bank details
  setBankDetails: (values: PaymentFormValues) => void
  // Actions - Address
  setHomeAddress: (value: string) => void
  setCity: (value: string) => void
  setCountry: (value: string) => void
  setPostalCode: (value: string) => void

  // Lifecycle
  seedClaim: (seed: ClaimSeed) => void
  resetClaim: () => void
}

// The default state mirrors the previous per-provider `useState` defaults so the
// behaviour is identical when entering the claim flow.
const defaultState = {
  claimRequested: '',
  claimId: '',
  treatment: '',
  treatmentCadence: '',
  additionalInfo: 'treatment-info',
  treatmentLabel: '',
  docType: 'Invoice',
  documents: [] as Document[],
  documentDescription: '',
  docIdType: '',
  idDocuments: [] as Document[],
  isConsetSelected: false,
  isMemberIDRemoved: false,
  existingDocuments: [] as ExistingClaimDocument[],
  medicallyNecessary: false,
  bankDetails: defaultBankDetails,
  homeAddress: '',
  city: '',
  country: '',
  postalCode: '',
}

/**
 * Zustand store for the reimbursement claim flow.
 * Replaces ClaimContext, ClaimTreatmentContext, ClaimDocumentContext,
 * ClaimMedicalInfoContext, ClaimBankDetailsContext and ClaimAddressContext.
 *
 * Because the store is global (unlike the previous per-mount providers), the
 * claim flow must seed it on entry and reset it on exit to avoid leaking data
 * across claims. See `seedClaim`/`resetClaim`.
 */
export const useClaimStore = create<ClaimState>()((set) => ({
  ...defaultState,

  setClaimRequested: (value) => set({claimRequested: value}),
  setClaimId: (value) => set({claimId: value}),

  setTreatment: (value) => set({treatment: value}),
  setTreatmentCadence: (value) => set({treatmentCadence: value}),
  setAdditionalInfo: (value) => set({additionalInfo: value}),
  setTreatmentLabel: (value) => set({treatmentLabel: value}),

  setDocType: (value) => set({docType: value}),
  setDocuments: (value) => set({documents: value}),
  setDocumentDescription: (value) => set({documentDescription: value}),
  setDocIdType: (value) => set({docIdType: value}),
  setIdDocuments: (value) => set({idDocuments: value}),
  setConsetSelected: (value) => set({isConsetSelected: value}),
  setMemberIDRemoved: (value) => set({isMemberIDRemoved: value}),
  setExistingDocuments: (value) => set({existingDocuments: value}),

  setMedicallyNecessary: (value) => set({medicallyNecessary: value}),

  setBankDetails: (values) => set({bankDetails: values}),

  setHomeAddress: (value) => set({homeAddress: value}),
  setCity: (value) => set({city: value}),
  setCountry: (value) => set({country: value}),
  setPostalCode: (value) => set({postalCode: value}),

  seedClaim: (seed) =>
    set({
      ...defaultState,
      claimRequested: seed.claimRequested ?? defaultState.claimRequested,
      claimId: seed.claimId ?? defaultState.claimId,
      treatment: seed.treatment ?? defaultState.treatment,
      treatmentCadence: seed.treatmentCadence ?? defaultState.treatmentCadence,
      additionalInfo: seed.additionalInfo ?? defaultState.additionalInfo,
      existingDocuments:
        seed.existingDocuments ?? defaultState.existingDocuments,
    }),

  resetClaim: () => set({...defaultState}),
}))

// Slice hooks scoped to each part of the claim flow. Each returns only the
// relevant state and actions using a shallow selector to avoid needless
// re-renders.
export const useClaim = () =>
  useClaimStore(
    useShallow((state) => ({
      claimRequested: state.claimRequested,
      setClaimRequested: state.setClaimRequested,
      claimId: state.claimId,
      setClaimId: state.setClaimId,
    }))
  )

export const useClaimTreatment = () =>
  useClaimStore(
    useShallow((state) => ({
      treatment: state.treatment,
      setTreatment: state.setTreatment,
      treatmentCadence: state.treatmentCadence,
      setTreatmentCadence: state.setTreatmentCadence,
      additionalInfo: state.additionalInfo,
      setAdditionalInfo: state.setAdditionalInfo,
      treatmentLabel: state.treatmentLabel,
      setTreatmentLabel: state.setTreatmentLabel,
    }))
  )

export const useClaimDocuments = () =>
  useClaimStore(
    useShallow((state) => ({
      docType: state.docType,
      setDocType: state.setDocType,
      documents: state.documents,
      setDocuments: state.setDocuments,
      documentDescription: state.documentDescription,
      setDocumentDescription: state.setDocumentDescription,
      docIdType: state.docIdType,
      setDocIdType: state.setDocIdType,
      idDocuments: state.idDocuments,
      setIdDocuments: state.setIdDocuments,
      isConsetSelected: state.isConsetSelected,
      setConsetSelected: state.setConsetSelected,
      isMemberIDRemoved: state.isMemberIDRemoved,
      setMemberIDRemoved: state.setMemberIDRemoved,
      existingDocuments: state.existingDocuments,
      setExistingDocuments: state.setExistingDocuments,
    }))
  )

export const useClaimMedicalInfo = () =>
  useClaimStore(
    useShallow((state) => ({
      medicallyNecessary: state.medicallyNecessary,
      setMedicallyNecessary: state.setMedicallyNecessary,
    }))
  )

export const useClaimBankDetails = () =>
  useClaimStore(
    useShallow((state) => ({
      bankDetails: state.bankDetails,
      setBankDetails: state.setBankDetails,
    }))
  )

export const useClaimAddress = () =>
  useClaimStore(
    useShallow((state) => ({
      homeAddress: state.homeAddress,
      setHomeAddress: state.setHomeAddress,
      city: state.city,
      setCity: state.setCity,
      country: state.country,
      setCountry: state.setCountry,
      postalCode: state.postalCode,
      setPostalCode: state.setPostalCode,
    }))
  )
